import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import type { QuestionType } from '@gokrew/models/survey';
import {
  getMySurveys,
  getEmployeeSurveyDashboard,
  getSurveyToTake,
  startSurvey,
  saveSurveyAnswers,
  submitSurvey,
} from '@/api/employee-survey';
import type {
  EmployeeSurveyDetail,
  EmployeeSurveyDashboard,
  EmployeeAnswerFile,
  SurveyAnswerValue,
  GetMySurveysParams,
  RawSurveyRecipient,
} from '@/types/employee-survey';
import { useEmployeePortalAuth } from '@/context/EmployeeAuthContext';
import { QueryKeys, TranslationNS } from '@gokrew/models/constants';
import type { ApiResponse, GetListResponse } from '@gokrew/models/generic';

export function useMySurveys(params: GetMySurveysParams) {
  const { user } = useEmployeePortalAuth();

  return useQuery<GetListResponse<RawSurveyRecipient>>({
    queryKey: [QueryKeys.EmployeeSurveysList, params],
    queryFn: () => getMySurveys(params),
    enabled: !!user,
    placeholderData: keepPreviousData,
  });
}

export function useEmployeeSurveyDashboard() {
  const { user } = useEmployeePortalAuth();

  return useQuery<ApiResponse<EmployeeSurveyDashboard>>({
    queryKey: [QueryKeys.EmployeeSurveyDashboard],
    queryFn: () => getEmployeeSurveyDashboard(),
    enabled: !!user,
  });
}

export function useSurveyToTake(surveyId: string | undefined) {
  const { user } = useEmployeePortalAuth();

  return useQuery<ApiResponse<EmployeeSurveyDetail>>({
    queryKey: [QueryKeys.EmployeeSurveyDetail, surveyId],
    queryFn: () => getSurveyToTake(surveyId!),
    enabled: !!user && !!surveyId,
  });
}

export function useStartSurvey() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (surveyId: string) => startSurvey(surveyId),
    onSuccess: (_, surveyId) => {
      qc.invalidateQueries({
        queryKey: [QueryKeys.EmployeeSurveyDetail, surveyId],
      });
    },
  });
}

export function useSaveSurveyAnswers() {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Survey);

  return useMutation({
    mutationFn: ({
      surveyId,
      answers,
      questionTypes,
      fileAnswers,
      savedQuestionIds,
    }: {
      surveyId: string;
      answers: Record<string, SurveyAnswerValue>;
      questionTypes: Map<string, QuestionType>;
      fileAnswers: Record<string, EmployeeAnswerFile[]>;
      /** Questions the server already has an answer for (QA-07). */
      savedQuestionIds?: ReadonlySet<string>;
      silent?: boolean;
    }) =>
      saveSurveyAnswers(
        surveyId,
        answers,
        questionTypes,
        fileAnswers,
        savedQuestionIds
      ),
    onSuccess: (_, { surveyId, silent }) => {
      if (!silent) toast.success(t('toast.draft_saved'));
      qc.invalidateQueries({
        queryKey: [QueryKeys.EmployeeSurveyDetail, surveyId],
      });
      qc.invalidateQueries({ queryKey: [QueryKeys.EmployeeSurveysList] });
    },
    onError: () => {
      toast.error(t('toast.error'));
    },
  });
}

export function useSubmitSurvey() {
  const qc = useQueryClient();
  const { t } = useTranslation(TranslationNS.Survey);

  return useMutation({
    mutationFn: (surveyId: string) => submitSurvey(surveyId),
    onSuccess: (_, surveyId) => {
      toast.success(t('toast.submitted'));
      qc.invalidateQueries({
        queryKey: [QueryKeys.EmployeeSurveyDetail, surveyId],
      });
      qc.invalidateQueries({ queryKey: [QueryKeys.EmployeeSurveysList] });
    },
    onError: () => {
      toast.error(t('toast.error'));
    },
  });
}
